import React, { useState } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Tooltip,
    Typography,
    IconButton,
} from "@mui/material";
import {
    ChevronLeft,
    ChevronRight,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { Building2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import PageRoutes from "../Routes/PageRoutes";
import CategoryIcon from "@mui/icons-material/Category";
import PeopleIcon from "@mui/icons-material/People";
import ImageIcon from "@mui/icons-material/Image";
import ArticleIcon from "@mui/icons-material/Article";
import StoreIcon from "@mui/icons-material/Store";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DashboardIcon from '@mui/icons-material/Dashboard';
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import EventNoteIcon from '@mui/icons-material/EventNote';
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

const drawerWidth = 260;

const Sidebar = ({ onLogout }) => {
    const [openSidebar, setOpenSidebar] = useState(true);
    const [expandedItems, setExpandedItems] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Menu items inside the component to access onLogout
    const menuItems = [
        { label: "Dashboard", icon: <DashboardIcon />, link: "/" },
        { label: "Categories", icon: <CategoryIcon />, link: "/getadmincategory" },
        { label: "Bookings", icon: <EventNoteIcon />, link: "/bookings" },
        { label: "Coupons", icon: <LocalOfferIcon />, link: "/getadmincoupons" },
        { label: "User", icon: <PeopleIcon />, link: "/users" },
        { label: "Banners", icon: <ImageIcon />, link: "/HomeBannersList" },
        { label: "Cms Content", icon: <ArticleIcon />, link: "/CmsContentList" },
        { label: "Stores", icon: <StoreIcon />, link: "/GetAdminStores" },
        { label: "Timeslots", icon: <AccessTimeIcon />, link: "/getadmintimeslot" },
        { label: "Extra Fees", icon: <MonetizationOnIcon />, link: "/extra-fees" },
        { label: "Logout", icon: <ExitToAppIcon />, color: "error", action: onLogout },
    ];

    const toggleExpand = (label) => {
        setExpandedItems(prev =>
            prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
        );
    };

    const renderMenuItem = (item, depth = 0) => {
        const hasChildren = Array.isArray(item.children);
        const isExpanded = expandedItems.includes(item.label);
        const isSelected = item.link && location.pathname === item.link;

        return (
            <React.Fragment key={item.label}>
                <ListItem
                    button
                    onClick={() => {
                        if (hasChildren) toggleExpand(item.label);
                        else if (item.link) navigate(item.link);
                        else if (item.action) item.action();
                    }}
                    selected={isSelected}
                    sx={{
                        pl: 2 + depth * 2,
                        backgroundColor: isSelected ? "#7e60bf" : "transparent",
                        "&:hover": {
                            backgroundColor: isSelected ? "#5d5ba8" : "rgba(112,110,191,0.1)",
                        },
                        borderRadius: "6px",
                        my: 0.5,
                    }}
                >
                    <Tooltip title={!openSidebar ? item.label : ""} placement="right">
                        <ListItemIcon
                            sx={{
                                color: "#fff",
                                minWidth: 40,
                                "& svg": { width: 20, height: 20 },
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                    </Tooltip>

                    {openSidebar && (
                        <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                                color: "#fff",
                                fontSize: depth === 0 ? "1rem" : "0.9rem",
                                fontWeight: depth === 0 ? 600 : 400,
                            }}
                        />
                    )}

                    {hasChildren && openSidebar && (
                        <Box sx={{ color: "#fff" }}>
                            {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                    )}
                </ListItem>

                {hasChildren && (
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {item.children.map(child => renderMenuItem(child, depth + 1))}
                    </Collapse>
                )}
            </React.Fragment>
        );
    };

    return (
        <Box sx={{ display: "flex" }}>
            <Drawer
                variant="permanent"
                open={openSidebar}
                sx={{
                    width: openSidebar ? drawerWidth : 80,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: openSidebar ? drawerWidth : 80,
                        boxSizing: "border-box",
                        backgroundColor: "#111",
                        color: "#fff",
                        overflow: "hidden",
                    },
                }}
            >
                {/* Sidebar Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: openSidebar ? "space-between" : "center",
                        p: 2,
                        bgcolor: "#111",
                        borderBottom: "1px solid #333",
                    }}
                >
                    {openSidebar ? (
                        <Typography variant="h6" fontWeight="bold" color="#fff">
                            Earnivo Cuts
                        </Typography>
                    ) : (
                        <Building2 size={18} />
                    )}
                    <IconButton
                        onClick={() => setOpenSidebar(!openSidebar)}
                        sx={{ color: "#fff" }}
                    >
                        {openSidebar ? <ChevronLeft /> : <ChevronRight />}
                    </IconButton>
                </Box>

                {/* Sidebar Menu */}
                <List disablePadding sx={{ overflowY: "auto", flexGrow: 1, cursor: 'pointer' }}>
                    {menuItems.map(item => renderMenuItem(item))}
                </List>
            </Drawer>

            {/* Main Content */}
            <Box component="main"
                sx={{ flexGrow: 1, width: "100%", transition: "margin 0.3s", padding: "10px 5px 0px 10px" }}
            >
                <PageRoutes />
            </Box>
        </Box>
    );
};

export default Sidebar;
